import { useQuery } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { EstadoCuentaTercero } from "@/componentes/ingresos/EstadoCuentaTercero"
import type { CuentaCliente, CuentaEmpleado } from "@/componentes/ingresos/tipos"
import { aCentimos, monto } from "@/componentes/tesoreria/dinero"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

/** Una línea por moneda; sin cifra, un guion. */
function PorMoneda({ filas }: { filas: { divisa: string; valor: string }[] }) {
  const con = filas.filter((f) => aCentimos(f.valor) !== 0n)
  if (con.length === 0) return <>—</>
  return (
    <>
      {con.map((f) => (
        <span key={f.divisa} className="block">
          {monto(f.valor)} {f.divisa}
        </span>
      ))}
    </>
  )
}

function Fallo({ que }: { que: string }) {
  return (
    <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
      <AlertCircle size={16} /> No se pudieron cargar las cuentas de {que}.
    </p>
  )
}

/**
 * La cuenta de cada cliente: lo que debe en CxC abiertas y lo que tiene de anticipo, por
 * moneda. Se deriva de sus CxC y de sus ingresos; nada se teclea aquí.
 */
export function CuentasClientes() {
  const [abierta, setAbierta] = useState<CuentaCliente | null>(null)
  const cuentas = useQuery({
    queryKey: ["cuentas-clientes"],
    queryFn: () => api.get<CuentaCliente[]>("/api/v1/cuentas-clientes"),
  })
  if (cuentas.isPending) return <p className="py-6">Cargando…</p>
  if (cuentas.isError) return <Fallo que="clientes" />
  const documento = (): DocumentoExportable => ({
    titulo: "Cuentas de clientes",
    subtitulo: "CxC abiertas y anticipos de cada cliente, por moneda",
    secciones: [
      {
        titulo: "Cuenta de cada cliente",
        columnas: [
          { titulo: "Cliente", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Pendiente", tipo: "monto" },
          { titulo: "Anticipo", tipo: "monto" },
          { titulo: "Neto", tipo: "monto" },
        ],
        filas: cuentas.data.flatMap((c) =>
          c.saldos.map((s) => ({
            celdas: [c.cliente, s.divisa, Number(s.pendiente), Number(s.anticipo), Number(s.neto)],
          })),
        ),
      },
    ],
  })
  return (
    <section className="space-y-4">
      {cuentas.data.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar
            titulo="Cuentas de clientes"
            subtitulo="CxC abiertas y anticipos de cada cliente, por moneda"
            documento={documento}
          />
        </div>
      )}
      <Tabla etiqueta="Cuenta de cada cliente">
        <Cabecera>
          <Th>Cliente</Th>
          <Th derecha>Pendiente</Th>
          <Th derecha>Anticipo</Th>
          <Th derecha>Neto</Th>
        </Cabecera>
        <Cuerpo>
          {cuentas.data.length === 0 && (
            <TablaVacia colSpan={4}>
              Ningún cliente tiene cuentas por cobrar abiertas ni anticipos.
            </TablaVacia>
          )}
          {cuentas.data.map((c) => (
            <Fila key={c.cliente_id} className="cursor-pointer" onClick={() => setAbierta(c)}>
              <Td>{c.cliente}</Td>
              <Td derecha numero>
                <PorMoneda
                  filas={c.saldos.map((s) => ({ divisa: s.divisa, valor: s.pendiente }))}
                />
              </Td>
              <Td derecha numero>
                <PorMoneda filas={c.saldos.map((s) => ({ divisa: s.divisa, valor: s.anticipo }))} />
              </Td>
              <Td derecha numero>
                <PorMoneda filas={c.saldos.map((s) => ({ divisa: s.divisa, valor: s.neto }))} />
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Neto positivo: el cliente debe. Negativo: tiene saldo a su favor. Pulsa un cliente para ver
        su estado de cuenta.
      </p>
      {abierta && (
        <EstadoCuentaTercero
          tipo="CLIENTE"
          id={abierta.cliente_id}
          nombre={abierta.cliente}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </section>
  )
}

/**
 * La cuenta de cada empleado: lo que debe por anticipos y préstamos pagados, menos lo
 * que ha devuelto con ingresos reportados.
 */
export function CuentasEmpleados() {
  const [abierta, setAbierta] = useState<CuentaEmpleado | null>(null)
  const cuentas = useQuery({
    queryKey: ["cuentas-empleados"],
    queryFn: () => api.get<CuentaEmpleado[]>("/api/v1/cuentas-empleados"),
  })
  if (cuentas.isPending) return <p className="py-6">Cargando…</p>
  if (cuentas.isError) return <Fallo que="empleados" />
  const documento = (): DocumentoExportable => ({
    titulo: "Cuentas de empleados",
    subtitulo: "Anticipos y préstamos pendientes de cada empleado, por moneda",
    secciones: [
      {
        titulo: "Cuenta de cada empleado",
        columnas: [
          { titulo: "Empleado", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Debe", tipo: "monto" },
        ],
        filas: cuentas.data.flatMap((c) =>
          c.saldos.map((s) => ({ celdas: [c.empleado, s.divisa, Number(s.debe)] })),
        ),
      },
    ],
  })
  return (
    <section className="space-y-4">
      {cuentas.data.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar
            titulo="Cuentas de empleados"
            subtitulo="Anticipos y préstamos pendientes de cada empleado, por moneda"
            documento={documento}
          />
        </div>
      )}
      <Tabla etiqueta="Cuenta de cada empleado">
        <Cabecera>
          <Th>Empleado</Th>
          <Th derecha>Debe</Th>
        </Cabecera>
        <Cuerpo>
          {cuentas.data.length === 0 && (
            <TablaVacia colSpan={2}>
              Ningún empleado debe nada. Un anticipo o un préstamo se marca al redactar el pago
              puntual en Nómina.
            </TablaVacia>
          )}
          {cuentas.data.map((c) => (
            <Fila key={c.persona_id} className="cursor-pointer" onClick={() => setAbierta(c)}>
              <Td>{c.empleado}</Td>
              <Td derecha numero>
                <PorMoneda filas={c.saldos.map((s) => ({ divisa: s.divisa, valor: s.debe }))} />
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {abierta && (
        <EstadoCuentaTercero
          tipo="EMPLEADO"
          id={abierta.persona_id}
          nombre={abierta.empleado}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </section>
  )
}
